<template>
  <div class="tw:relative tw:bg-white tw:rounded-lg tw:border tw:border-gray-300 tw:flex tw:flex-col tw:lg:flex-row">
    <div
      class="tw:flex tw:flex-row tw:items-start tw:md:items-center tw:justify-between tw:w-full tw:lg:w-[300px] tw:p-4 tw:pr-6"
    >
      <div class="tw:flex tw:items-start tw:md:items-center tw:flex-col tw:md:flex-row tw:w-full tw:gap-y-4">
        <div
          class="tw:w-auto tw:md:w-full tw:flex tw:flex-col tw:md:flex-row tw:gap-y-2 tw:justify-between tw:items-start tw:md:items-center tw:flex-wrap"
        >
          <div class="tw:flex tw:flex-col tw:items-start tw:pr-2 tw:lg:flex-[initial] tw:md:flex-[0_0_49%]">
            <h4 class="tw:text-sm tw:font-bold tw:text-gray-900">
              {{ $t('app.rateConnect.name') }}
            </h4>
            <div class="tw:flex tw:gap-x-1">
              <p class="tw:text-sm">
                {{ campaignType === 'cpc' ? $t('rc_campaign_cpc') : $t('rc_cpa_campaign') }}
              </p>
            </div>
          </div>
          <div class="tw:flex tw:items-center tw:gap-x-1 tw:lg:flex-[initial] tw:md:flex-[0_0_49%]">
            <UiTooltip
              theme="material"
              tooltip-position="top"
              :label="$t(hasTooltip(product.status))"
              v-if="hasTooltip(product.status)"
              class="tw:md:order-0 tw:order-1"
            >
              <InformationOutline class="tw:fill-none tw:stroke-gray-500 tw:h-[16px] tw:w-[16px] tw:ml-1" />
            </UiTooltip>
            <span
              :class="getStatusClasses(product.status)"
              class="tw:text-sm tw:font-bold tw:py-1 tw:px-2 tw:rounded tw:order-0 tw:md:order-1 tw:text-nowrap"
              >{{ $t(campaignStatusKeys[product.status]) }}</span
            >
          </div>
        </div>
        <MainButton size="medium" class="tw:mr-8 tw:flex tw:md:hidden" @click="checkCampaign">{{
          $t('rc_check_campaign_cta')
        }}</MainButton>
      </div>
      <div class="tw:flex tw:lg:hidden tw:justify-end tw:w-full tw:items-center tw:mt-4 tw:md:mt-0">
        <MainButton size="medium" class="tw:mr-8 tw:hidden tw:md:flex tw:lg:hidden" @click="checkCampaign">{{
          $t('rc_check_campaign_cta')
        }}</MainButton>
        <div class="tw:flex tw:justify-end tw:relative tw:min-w-[30px]" v-click-outside="closeMobileMenu">
          <button
            class="tw:hover:bg-gray-200 tw:rounded-md"
            @click="openMobileMenu"
            :class="{
              'tw:bg-gray-200 tw:rounded-md': mobileMenuVisible !== null && mobileMenuVisible === product.itemId,
            }"
          >
            <img :src="dots" />
          </button>
          <div
            v-show="mobileMenuVisible !== null && mobileMenuVisible === product.itemId"
            @click.stop
            class="tw:absolute tw:right-0 tw:top-[100%] tw:w-[300px] tw:w-[min(300px,calc(100vw-100px))] tw:bg-white shadow tw:mt-1 tw:rounded-lg tw:z-30 tw:max-h-[300px] tw:overflow-auto"
          >
            <div
              :class="product.status !== 'ACTIVE' ? 'tw:rounded-t-lg' : 'tw:rounded-lg'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800"
              @click="openInvoices"
            >
              {{ $t('tbs_invoice_review') }}
            </div>
            <div
              v-if="product.status === 'OBSOLETE'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:bg-white tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-b-lg"
              @click="startNewCampaign"
            >
              {{ $t('rc_start_new_campaign') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      class="tw:bg-gray-100 tw:py-4 tw:px-4 tw:lg:px-8 tw:flex tw:w-full tw:lg:w-[calc(100%-300px)] tw:rounded-b-lg tw:lg:rounded-r-lg"
    >
      <div class="tw:flex tw:flex-wrap tw:w-full tw:gap-4 tw:flex-[0_0_100%] tw:lg:flex-[0_0_70%]">
        <div class="tw:flex-[1_1_45%] tw:md:flex-[0_0_23%] tw:lg:flex-[0_0_30%] tw:flex tw:flex-col tw:gap-y-2">
          <p class="tw:text-sm tw:font-bold">{{ $t('subscription_billing') }}</p>
          <p class="tw:text-sm">
            {{ $t('subscription_billing_monthly') }}
          </p>
        </div>
        <div class="tw:flex-[1_1_45%] tw:md:flex-[0_0_23%] tw:lg:flex-[0_0_30%] tw:flex tw:flex-col tw:gap-y-2">
          <p class="tw:text-sm tw:font-bold">{{ $t('subscription_start') }}</p>
          <p class="tw:text-sm">{{ startDate }}</p>
        </div>
        <div class="tw:flex-[1_1_45%] tw:md:flex-[0_0_23%] tw:lg:flex-[0_0_30%] tw:flex tw:flex-col tw:gap-y-2">
          <p class="tw:text-sm tw:font-bold">
            {{ $t('subscriptions.nextPayment') }}
          </p>
          <p class="tw:text-sm">{{ nextPaymentDate }}</p>
        </div>
      </div>

      <div class="tw:hidden tw:lg:flex tw:justify-end tw:w-full tw:items-center tw:gap-x-4">
        <MainButton size="medium" @click="checkCampaign" v-if="product.status !== 'INACTIVE'">{{
          $t('rc_check_campaign_cta')
        }}</MainButton>
        <div class="tw:flex tw:justify-end" v-click-outside="closeMenu">
          <button
            class="tw:hover:bg-gray-200 tw:rounded-md tw:w-[36px] tw:h-[36px]"
            @click="openMenu"
            :class="{ 'tw:bg-gray-200 tw:rounded-md': menuVisible !== null && menuVisible === product.itemId }"
          >
            <img :src="dots" />
          </button>
          <div
            v-show="menuVisible !== null && menuVisible === product.itemId"
            @click.stop
            class="tw:absolute tw:right-0 tw:top-[100%] tw:w-[300px] tw:w-[min(300px,calc(100vw-100px))]tw:bg-white shadow tw:mt-1 tw:rounded-lg tw:z-30 tw:max-h-[300px] tw:overflow-auto tw:bg-white"
          >
            <div
              :class="product.status !== 'ACTIVE' ? 'tw:rounded-t-lg' : 'tw:rounded-lg'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:bg-white"
              @click="openInvoices"
            >
              {{ $t('tbs_invoice_review') }}
            </div>
            <div
              v-if="product.status === 'OBSOLETE'"
              class="tw:px-4 tw:py-3 tw:text-sm tw:bg-white tw:hover:bg-blue-200 tw:hover:text-blue-800 tw:rounded-b-lg"
              @click="startNewCampaign"
            >
              {{ $t('rc_start_new_campaign') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import UiTooltip from '@/components/dashboard/ui/UiTooltip.vue'
import MainButton from '@/components/base/buttons/MainButton.vue'
import InformationOutline from '@/components/icons/InformationOutline.svg?component'

import { useBudget, useCampaign, useCompetitivenessStats, usePartners } from '@/components/rateConnect/queries'
import { setSelectedAccommodationId } from '@/layouts/queries'
import { useInvoicesFilters } from '@/components/billing/invoices/useInvoicesFilters'
import { useFormatting } from '@/composables/useFormatting'

import appAnalytics from '@/utils/tracking'

const dots = new URL(`../../../assets/img/rate-connect/dropdown-dots.svg`, import.meta.url)

const router = useRouter()
const props = defineProps({
  product: Object,
  accommodationName: String,
})

const mobileMenuVisible = ref(null)
const menuVisible = ref(null)
const menuItemType = ref(null)
const currentItemId = computed(() => props.product.itemId)
const { formatDate } = useFormatting()
const { selectedPropertyValues, selectedProductValues, timeframeValue, setStartDate, setEndDate } = useInvoicesFilters()

const { competitivenessStats } = useCompetitivenessStats(currentItemId)
const { currentPartner, campaignsData } = useCampaign(currentItemId)
const { partnersData } = usePartners(currentItemId)
const campaignType = computed(() => campaignsData.value?.campaignType)
const { budget } = useBudget({ accommodationId: currentItemId, campaignType })

const campaignStatusKeys = computed(() => ({
  ACTIVE: 'rc_provider_status_active',
  PAUSED: campaignType.value === 'cpc' ? 'rc_settings_campaign_off' : 'rc_campaign_status_inactive',
  PAUSED_UNTIL: 'rc_settings_campaign_off',
  OBSOLETE: 'rc_provider_status_inactive',
  PAUSED_FOR_BUDGET: 'rc_cpc_budget_status',
}))

const currentRCPartner = computed(() => partnersData.value?.find(item => item.partnerId === currentPartner.value))
const unavailabilityRate = computed(() => {
  if (competitivenessStats.value?.unavailabilityShare === null) {
    return '-'
  } else {
    const sum =
      Math.round(competitivenessStats.value?.beatShare) +
      Math.round(competitivenessStats.value?.meetShare) +
      Math.round(competitivenessStats.value?.loseShare) +
      Math.round(competitivenessStats.value?.unavailabilityShare)

    if (sum > 100) {
      return Math.round(competitivenessStats.value?.unavailabilityShare) - 1
    } else if (sum < 100) {
      return Math.round(competitivenessStats.value?.unavailabilityShare) + 1
    } else {
      return Math.round(competitivenessStats.value?.unavailabilityShare)
    }
  }
})
const budgetPercentage = computed(() => {
  const percentage = Math.round((budget.value?.left / budget.value?.limit) * 100)
  return percentage
})
const partnerStatus = computed(() =>
  currentRCPartner.value?.partnerStatus === 5 && currentRCPartner.value?.connectionStatus === 5 ? 'active' : 'inactive'
)

const openMenu = () => {
  menuVisible.value = props.product.itemId
  menuItemType.value = props.product.type
}

const closeMenu = () => {
  menuVisible.value = null
  menuItemType.value = null
}

const openMobileMenu = () => {
  mobileMenuVisible.value = props.product.itemId
  menuItemType.value = props.product.type
}
const closeMobileMenu = () => {
  mobileMenuVisible.value = null
  menuItemType.value = null
}
const nextPaymentDate = computed(() => {
  const todayDayOfMonth = new Date().getDate()
  const month = todayDayOfMonth === 1 || todayDayOfMonth === 2 ? new Date().getMonth() : new Date().getMonth() + 1
  const day = 2
  const year = new Date().getFullYear()
  return formatDate(new Date(year, month, day))
})

const startDate = computed(() => {
  return formatDate(props.product?.startDate)
})

const checkCampaign = () => {
  appAnalytics.track(appAnalytics.events.BILLING_RC_CHECK_CAMPAIGN, {
    item_id: props.product.itemId,
  })

  setSelectedAccommodationId(props.product.itemId)
  router.push({ name: 'rate-connect-running' })
}
const startNewCampaign = () => {
  appAnalytics.track(appAnalytics.events.BILLING_RC_START_CAMPAIGN, {
    item_id: props.product.itemId,
  })

  router.push({ name: 'rate-connect-setup' })
}
const openInvoices = () => {
  appAnalytics.track(appAnalytics.events.BILLING_REVIEW_INVOICES, {
    item_id: props.product.itemId,
  })
  selectedPropertyValues.value = [
    { id: props.product.itemId, label: props.accommodationName, value: props.product.itemId },
  ]
  timeframeValue.value = { id: 'last-12-months', label: 'last_12_months', value: '12' }
  setStartDate({ id: 'last-12-months', label: 'last_12_months', value: '12' })
  setEndDate()
  selectedProductValues.value = [{ id: 'rate-connect', label: 'navigation_menu_rate_connect', value: 'RATE_CONNECT' }]
  router.push({ name: 'billing-invoices' })
}

const getStatusClasses = status => {
  if (status === 'OBSOLETE' || status === 'PAUSED' || status === 'PAUSED_UNTIL') {
    return 'tw:bg-red-200 tw:text-red-700'
  } else if (
    (campaignType.value === 'netCpa' && unavailabilityRate.value === 100) ||
    partnerStatus.value === 'inactive' ||
    (campaignType.value === 'cpc' && Number(budgetPercentage.value) <= 0) ||
    (campaignType.value === 'cpc' && isNaN(budgetPercentage.value)) ||
    status === 'PAUSED_FOR_BUDGET'
  ) {
    return 'tw:bg-gray-200 tw:text-gray-700'
  } else if (status === 'ACTIVE') {
    return 'tw:bg-green-200 tw:text-green-700'
  } else return ''
}

const hasTooltip = status => {
  if (status === 'INACTIVE') return 'rc_campaign_inactive_tooltip'
  else if (status === 'PAUSED' || status === 'PAUSED_UNTIL') {
    return 'rc_campaign_offline_tooltip'
  } else if (
    (campaignType.value === 'netCpa' && unavailabilityRate.value === 100) ||
    partnerStatus.value === 'inactive' ||
    (campaignType.value === 'cpc' && Number(budgetPercentage.value) <= 0) ||
    (campaignType.value === 'cpc' && isNaN(budgetPercentage.value))
  ) {
    return 'rc_campaign_offline_tooltip'
  } else if (currentRCPartner.value?.partnerStatus < 0) {
    return 'rc_campaign_inactive_tooltip'
  } else return false
}
</script>
